<template>
  <div class="docs-container">
    <!-- Main Content -->
    <div class="docs-main">
      <!-- Sidebar -->
      <aside class="docs-sidebar">
        <div class="sidebar-content">
          <h3 class="sidebar-title">Documentation</h3>
          <nav class="sidebar-nav">
            <ul class="nav-list" v-for="nav in sidebarArr" :key="nav.title">
              <li>
                <a :href="nav.link" class="nav-item">{{ nav.title }}</a>
                <ul
                  v-if="nav.children && nav.children.length"
                  class="sub-nav-list"
                >
                  <li v-for="child in nav.children" :key="child.title">
                    <a :href="child.link" class="nav-item sub">{{
                      child.title
                    }}</a>
                  </li>
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </aside>

      <!-- Content -->
      <main class="docs-content">
        <!-- 1. Overview -->
        <section id="overview" class="docs-section">
          <h1 class="section-title"><i>Scan SDK</i></h1>
          <!--<p class="section-intro">
            <i>
              Our SDK enables 3D scanning and capture functionality in your
              mobile applications. It supports both Android and iOS, with a
              unified API to create immersive 3D experiences.</i
            >
          </p>-->
        </section>
        <section class="docs-section">
          <div class="container">
            <div class="demo-video">
              <div class="video-app">
                <NuxtImg
                  :src="`${baseUrl}/static/images/sdk-demo-first-frame.png`"
                  alt="sdk-Demo"
                  preload
                  fetchPriority="high"
                  v-show="!isPlaying"
                />
                <MuxVideo
                  mux-id="Ejyh8KHcv9F501kKGdnHrhiUCn5dwWnHOXR6TDUrL4ME"
                  :blur="false"
                  @isPlaying="isPlaying = true"
                />
                <div class="clean-chip">
                  <div class="chip-content">
                    <p class="sub-text">
                      <i>App Interface</i>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="description">
              <p class="section-intro">
                With Travvir’s SDK, you can easily implement 360° scene
                capturing functionality directly within your mobile application,
                both on android & iOS.
              </p>
            </div>
          </div>
        </section>

        <!-- 2. Request Access -->
        <section id="request-access" class="docs-section">
          <h1 class="section-title"><i>Request SDK Access</i></h1>
          <p class="section-intro">
            <i>Fill out the form below to request SDK access & credentials.</i>
          </p>
          <div class="sdk-request-form">
            <div class="form-container">
              <form @submit.prevent="submitSDKRequest" class="request-form">
                <div class="form-group">
                  <!-- <label for="developerName">Name *</label> -->
                  <input
                    type="text"
                    id="developerName"
                    v-model="Name"
                    placeholder="Name*"
                    required
                  />
                </div>

                <div class="form-group">
                  <!-- <label for="developerEmail">Official Email Address *</label> -->
                  <input
                    type="email"
                    id="developerEmail"
                    v-model="Email"
                    placeholder="Official email address*"
                    required
                  />
                </div>

                <button type="submit" class="submit-btn" :disabled="submit">
                  {{ submit ? "Submitting..." : "Request Access" }}
                </button>

                <p class="form-note" v-if="flash">{{ InvalidText }}</p>
              </form>
            </div>
          </div>
        </section>

        <!-- 3. Android SDK -->
        <section id="android-sdk" class="docs-section">
          <h2 class="section-heading"><i>Android SDK</i></h2>
          <!-- 3.1 Requirements -->
          <section id="android-requirements" class="docs-subsection">
            <h3 class="subsection-heading">Requirements</h3>
            <div class="requirements-grid">
              <div class="requirement-card" v-for="card in CardDetails">
                <h3>{{ card.title }}</h3>
                <ul class="requirement-list" v-for="info in card.Detail">
                  <li>
                    <strong>{{ info.label }}</strong
                    ><i>{{ info.link }}</i>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 3.2 Installation -->
          <section id="android-installation" class="docs-subsection">
            <h3 class="subsection-heading">Installation</h3>
            <div class="installation-steps">
              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">1) Add Maven Central Repository</h4>
                  <p>
                    <i>
                      Ensure Maven Central is included in your project's
                      <code>`settings.gradle.kts`</code>(or
                      <code>`build.gradle.kts`</code> for older projects):
                    </i>
                  </p>
                  <div class="code-block">
                    <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code>dependencyResolutionManagement {
    repositories {
       google()
       mavenCentral()
    }
}</code></span></pre>
                    <div class="code-action">
                      <RiFileCopyLine
                        size="1.3rem"
                        class="copy-icon"
                        @click="copyToClipboard($event.target)"
                        title="Copy code"
                      />
                    </div>
                  </div>
                  <div class="notes">
                    <div class="note-content">
                      <strong>Note:</strong>
                      <p>
                        Maven Central is typically included by default in modern
                        Android projects. If you're using an older project
                        structure, add
                        <code>mavenCentral()</code> to your
                        <code>build.gradle</code>
                        (Project level).
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">2) Add Dependency</h4>
                  <p>
                    <i
                      >Add the SDK dependency to your app's
                      <code>build.gradle.kts</code> (Module level)</i
                    >
                  </p>
                  <div class="code-block">
                    <pre>
<span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code>dependencies {
    implementation(<span class="hljs-string">"com.travvir:sdk:1.0.1"</span>)

    <span class="hljs-comment">// Required dependencies</span>
    implementation(<span class="hljs-string">"androidx.core:core-ktx:1.15.0"</span>)
    implementation(<span class="hljs-string">"androidx.appcompat:appcompat:1.7.1"</span>)
    implementation(<span class="hljs-string">"androidx.activity:activity-ktx:1.10.1"</span>)
}</code></span>
                    </pre>
                    <div class="code-action">
                      <RiFileCopyLine
                        size="1.3rem"
                        class="copy-icon"
                        @click="copyToClipboard($event.target)"
                        title="Copy code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">3) Sync Project</h4>
                  <p>
                    <i
                      >Sync your Gradle files to download the SDK from Maven
                      Central:</i
                    >
                  </p>
                  <div class="code-block">
                    <ul class="sync-actions">
                      <li>Click <b>"Sync Now"</b> in the notification bar.</li>
                      <li>
                        Or select
                        <b>File → Sync Project with Gradle Files</b> from the
                        menu.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">4) Update AndroidManifest.xml</h4>
                  <p><i>Add the required permissions and features:</i></p>
                  <div class="code-block">
                    <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-comment">&lt;!-- Required permissions --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span> <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.CAMERA"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span> <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.INTERNET"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span> <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.WAKE_LOCK"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span> <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.FOREGROUND_SERVICE"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span> <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.FOREGROUND_SERVICE_DATA_SYNC"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span> <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.POST_NOTIFICATIONS"</span> /&gt;</span>

<span class="hljs-comment">&lt;!-- Storage permission for Android 10 and below --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-permission</span>
    <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.permission.WRITE_EXTERNAL_STORAGE"</span>
    <span class="hljs-attr">android:maxSdkVersion</span>=<span class="hljs-string">"29"</span> /&gt;</span>

<span class="hljs-comment">&lt;!-- Required hardware features --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-feature</span>
    <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.hardware.sensor.accelerometer"</span>
    <span class="hljs-attr">android:required</span>=<span class="hljs-string">"true"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-feature</span>
    <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.hardware.sensor.gyroscope"</span>
    <span class="hljs-attr">android:required</span>=<span class="hljs-string">"true"</span> /&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-feature</span>
    <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.hardware.camera"</span>
    <span class="hljs-attr">android:required</span>=<span class="hljs-string">"true"</span> /&gt;</span>

<span class="hljs-comment">&lt;!-- Optional hardware features --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">uses-feature</span>
    <span class="hljs-attr">android:name</span>=<span class="hljs-string">"android.hardware.camera.autofocus"</span>
    <span class="hljs-attr">android:required</span>=<span class="hljs-string">"false"</span> /&gt;</span></code></span></pre>
                    <div class="code-action">
                      <RiFileCopyLine
                        size="1.3rem"
                        class="copy-icon"
                        @click="copyToClipboard($event.target)"
                        title="Copy code"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 3.3 Quick Start -->
          <section id="android-quick-start" class="docs-subsection">
            <h3 class="subsection-heading">Quick Start</h3>
            <div class="quick-start-steps">
              <div class="quick-step">
                <h4 class="step-heading">1) Initialize Session</h4>
                <div class="code-block">
                  <pre
                    class="theme-github-dark-dimmed shadow-3xl text-sm relative overflow-hidden max-w-full tab-size h-full"
                  ><span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code><span class="hljs-keyword">import</span> com.travvir.sdk.api.ApiClient
<span class="hljs-keyword">import</span> com.travvir.sdk.api.SessionCreationResult
<span class="hljs-keyword">import</span> com.travvir.sdk.activity.Camera3DActivity

<span class="hljs-keyword">class</span> <span class="hljs-title class_">MainActivity</span> : <span class="hljs-type">AppCompatActivity</span>() {
    <span class="hljs-keyword">private</span> <span class="hljs-keyword">val</span> sdkKey = <span class="hljs-string">"your-sdk-key-here"</span> <span class="hljs-comment">// Replace with your actual SDK key</span>

    <span class="hljs-keyword">private</span> <span class="hljs-function"><span class="hljs-keyword">fun</span> <span class="hljs-title">startScanning</span><span class="hljs-params">()</span></span> {
        ApiClient.generateSessionId(
            context = <span class="hljs-keyword">this</span>,
            sdkKey = sdkKey
        ) { result -&gt;
            <span class="hljs-keyword">when</span> (result) {
                <span class="hljs-keyword">is</span> SessionCreationResult.Success -&gt; {
                    launchCamera(result.sessionId)
                }
                <span class="hljs-keyword">is</span> SessionCreationResult.Error -&gt; {
                    <span class="hljs-comment">// Handle error</span>
                    handleError(result.error)
                }
            }
        }
    }

    <span class="hljs-keyword">private</span> <span class="hljs-function"><span class="hljs-keyword">fun</span> <span class="hljs-title">launchCamera</span><span class="hljs-params">(sessionId: <span class="hljs-type">String</span>)</span></span> {
        <span class="hljs-keyword">val</span> intent = Intent(<span class="hljs-keyword">this</span>, Camera3DActivity::<span class="hljs-keyword">class</span>.java).apply {
            putExtra(<span class="hljs-string">"sessionId"</span>, sessionId)
            putExtra(<span class="hljs-string">"sdkKey"</span>, sdkKey)
        }
        startActivity(intent)
    }
}</code></span><small class="bg-black/30 absolute top-0 right-0 uppercase font-bold text-xs rounded-bl-md px-2 py-1"></small></pre>
                  <div class="code-action">
                    <RiFileCopyLine
                      size="1.3rem"
                      class="copy-icon"
                      @click="copyToClipboard($event.target)"
                      title="Copy code"
                    />
                  </div>
                </div>
              </div>

              <div class="quick-step">
                <h4 class="step-heading">2) Handle Permissions</h4>
                <div class="code-block">
                  <pre>
<span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code><span class="hljs-keyword">private</span> <span class="hljs-keyword">val</span> requiredPermissions = mutableListOf&lt;String&gt;().apply {
    add(Manifest.permission.CAMERA)
    <span class="hljs-keyword">if</span> (Build.VERSION.SDK_INT &gt;= Build.VERSION_CODES.TIRAMISU) {
        add(Manifest.permission.POST_NOTIFICATIONS)
    }
    <span class="hljs-keyword">if</span> (Build.VERSION.SDK_INT &lt;= Build.VERSION_CODES.Q) {
        add(Manifest.permission.WRITE_EXTERNAL_STORAGE)
    }
}

<span class="hljs-keyword">private</span> <span class="hljs-keyword">val</span> permissionLauncher = registerForActivityResult(
    ActivityResultContracts.RequestMultiplePermissions()
) { permissions -&gt;
    <span class="hljs-keyword">val</span> deniedPermissions = permissions.filterValues { !it }.keys
    <span class="hljs-keyword">if</span> (deniedPermissions.isEmpty()) {
        startScanning()
    } <span class="hljs-keyword">else</span> {
        handlePermissionsDenied(deniedPermissions)
    }
}</code></span>
                  </pre>
                  <div class="code-action">
                    <RiFileCopyLine
                      size="1.3rem"
                      class="copy-icon"
                      @click="copyToClipboard($event.target)"
                      title="Copy code"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 3.4 API References -->
          <section id="android-api" class="docs-subsection">
            <h3 class="subsection-heading">API References</h3>
            <div class="api-section">
              <h4>Client SDK</h4>
              <div class="code-block">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code>ApiClient.generateSessionId(
    context: Context,                        <span class="hljs-comment">// Application/Activity context</span>
    sdkKey: String,                          <span class="hljs-comment">// Your SDK key</span>
    sceneName: String? = <span class="hljs-literal">null</span>,               <span class="hljs-comment">// Optional scene name</span>
    metadata: Map&lt;String, String&gt;? = <span class="hljs-literal">null</span>,   <span class="hljs-comment">// Optional metadata</span>
    callback: (SessionCreationResult) -&gt; <span class="hljs-built_in">Unit</span> <span class="hljs-comment">// Result callback</span>
)</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
              <h4>Response Types</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-keyword">sealed</span> <span class="hljs-keyword">class</span> <span class="hljs-title class_">SessionCreationResult</span> {
    <span class="hljs-keyword">data</span> <span class="hljs-keyword">class</span> <span class="hljs-title class_">Success</span>(<span class="hljs-keyword">val</span> sessionId: String) : SessionCreationResult()
    <span class="hljs-keyword">data</span> <span class="hljs-keyword">class</span> <span class="hljs-title class_">Error</span>(<span class="hljs-keyword">val</span> error: String) : SessionCreationResult()
}</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
              <h4>Camera</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-keyword">val</span> intent = Intent(context, Camera3DActivity::<span class="hljs-keyword">class</span>.java).apply {
  putExtra(<span class="hljs-string">"sessionId"</span>, sessionId)
  putExtra(<span class="hljs-string">"sdkKey"</span>, sdkKey)
}
startActivity(intent)</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
              <h4>Upload Management</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-comment">// Start upload service manually (usually automatic)</span>
Utility.startUploadService(<span class="hljs-keyword">this</span>, sdkKey)

<span class="hljs-comment">// Stop upload service to conserve battery/bandwidth</span>
Utility.stopUploadService(<span class="hljs-keyword">this</span>)

<span class="hljs-comment">// Check upload progress</span>
Utility.getUploadProgress(context, sessionId) { progress -&gt;
    <span class="hljs-comment">// -1 = no files, 0..100 = progress</span>
}</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
            </div>
          </section>
        </section>

        <!-- 4. iOS SDK -->
        <section id="ios-sdk" class="docs-section">
          <h2 class="section-heading"><i>iOS SDK</i></h2>
          <!-- 4.1 Requirements -->
          <section id="ios-requirements" class="docs-subsection">
            <h3 class="subsection-heading">Requirements</h3>
            <div class="requirements-grid">
              <div class="requirement-card">
                <h3>Environment</h3>
                <ul class="requirement-list">
                  <li><strong>Version</strong><i>: iOS 16.0+</i></li>
                  <li><strong>Xcode</strong><i>: 15.0+</i></li>
                  <li><strong>Swift</strong><i>: 5.9+</i></li>
                </ul>
              </div>
              <div class="requirement-card">
                <h3>Permissions</h3>
                <ul class="requirement-list">
                  <li><strong>Camera</strong></li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 4.2 Installation -->
          <section id="ios-installation" class="docs-subsection">
            <h3 class="subsection-heading">Installation</h3>
            <div class="installation-steps">
              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">1) Add Framework to Project</h4>
                  <ol>
                    <li>Extract the provided ZIP file</li>
                    <li>
                      Drag and drop <code>TravvirSDK.xcframework</code> into
                      your Xcode project
                    </li>
                    <li>
                      Select <b>Copy files to destination</b> (or
                      <b>Move files to destination</b>)
                    </li>
                    <li>
                      Ensure your app target is selected under <b>Targets</b>
                    </li>
                  </ol>
                </div>
              </div>
              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">2) Configure Framework Settings</h4>
                  <p>
                    Project ➜ Target ➜ General ➜ Frameworks, Libraries, and
                    Embedded Content:
                  </p>
                  <ul>
                    <li>Find <code>TravvirSDK.xcframework</code></li>
                    <li>Set embed option to <b>Embed & Sign</b></li>
                  </ul>
                </div>
              </div>
              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">3) Update Info.plist</h4>
                  <div class="code-block">
                    <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-operator">&lt;</span>key<span class="hljs-operator">&gt;</span><span class="hljs-type">NSCameraUsageDescription</span>&lt;/key&gt;
<span class="hljs-operator">&lt;</span>string<span class="hljs-operator">&gt;</span><span class="hljs-type">This</span> app needs camera access to perform 3D scanning<span class="hljs-operator">&lt;/</span>string<span class="hljs-operator">&gt;</span></code></span></pre>
                    <div class="code-action">
                      <RiFileCopyLine
                        size="1.3rem"
                        class="copy-icon"
                        @click="copyToClipboard($event.target)"
                        title="Copy code"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="quick-step">
                <div class="step-content">
                  <h4 class="step-heading">4) Import Framework</h4>
                  <div class="code-block">
                    <pre>
                      <span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-keyword">import</span> TravvirSDK</code></span>
                    </pre>
                    <div class="code-action">
                      <RiFileCopyLine
                        size="1.3rem"
                        class="copy-icon"
                        @click="copyToClipboard($event.target)"
                        title="Copy code"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 4.3 Quick Start -->
          <section id="ios-quick-start" class="docs-subsection">
            <h3 class="subsection-heading">Quick Start</h3>
            <div class="quick-start-steps">
              <div class="quick-step">
                <h4 class="step-heading">1) Initialize Session</h4>
                <div class="code-block">
                  <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code><span class="hljs-keyword">import</span> SwiftUI
<span class="hljs-keyword">import</span> TravvirSDK
<span class="hljs-keyword">import</span> AVFoundation

<span class="hljs-keyword">struct</span> <span class="hljs-title class_">ContentView</span>: <span class="hljs-title class_ inherited__">View</span> {
    <span class="hljs-meta">@State</span> <span class="hljs-keyword">private</span> <span class="hljs-keyword">var</span> sessionId: <span class="hljs-type">String</span>?
    <span class="hljs-meta">@State</span> <span class="hljs-keyword">private</span> <span class="hljs-keyword">var</span> showingCamera <span class="hljs-operator">=</span> <span class="hljs-literal">false</span>
    <span class="hljs-meta">@State</span> <span class="hljs-keyword">private</span> <span class="hljs-keyword">var</span> cameraPermissionGranted <span class="hljs-operator">=</span> <span class="hljs-literal">false</span>

    <span class="hljs-keyword">private</span> <span class="hljs-keyword">let</span> sdkKey <span class="hljs-operator">=</span> <span class="hljs-string">"your-sdk-key-here"</span> <span class="hljs-comment">// Replace with your actual SDK key</span>

    <span class="hljs-keyword">var</span> body: <span class="hljs-keyword">some</span> <span class="hljs-type">View</span> {
        <span class="hljs-type">VStack</span> {
            <span class="hljs-keyword">if</span> cameraPermissionGranted {
                <span class="hljs-type">Button</span>(<span class="hljs-string">"Start 3D Scanning"</span>) { generateSession() }
                    .padding().background(<span class="hljs-type">Color</span>.blue).foregroundColor(.white).cornerRadius(<span class="hljs-number">10</span>)
            } <span class="hljs-keyword">else</span> {
                <span class="hljs-type">Button</span>(<span class="hljs-string">"Grant Camera Permission"</span>) { requestCameraPermission() }
                    .padding().background(<span class="hljs-type">Color</span>.orange).foregroundColor(.white).cornerRadius(<span class="hljs-number">10</span>)
            }
        }
        .fullScreenCover(isPresented: <span class="hljs-variable">$showingCamera</span>) {
            <span class="hljs-keyword">if</span> <span class="hljs-keyword">let</span> sessionId <span class="hljs-operator">=</span> sessionId {
                <span class="hljs-type">Camera3DView</span>(sessionId: sessionId, sdkKey: sdkKey)
            }
        }
        .onAppear { checkCameraPermission() }
    }

    <span class="hljs-keyword">private</span> <span class="hljs-keyword">func</span> <span class="hljs-title function_">generateSession</span>() {
        <span class="hljs-type">ApiClient</span>.generateSessionId(
            sdkKey: sdkKey,
            sceneName: <span class="hljs-string">"My 3D Scan"</span>,
            metadata: [<span class="hljs-string">"app_version"</span>: <span class="hljs-string">"1.0.0"</span>]
        ) { error, sessionId <span class="hljs-keyword">in</span>
            <span class="hljs-keyword">if</span> <span class="hljs-keyword">let</span> sessionId <span class="hljs-operator">=</span> sessionId { <span class="hljs-keyword">self</span>.sessionId <span class="hljs-operator">=</span> sessionId; <span class="hljs-keyword">self</span>.showingCamera <span class="hljs-operator">=</span> <span class="hljs-literal">true</span> }
        }
    }
}</code></span></pre>
                  <div class="code-action">
                    <RiFileCopyLine
                      size="1.3rem"
                      class="copy-icon"
                      @click="copyToClipboard($event.target)"
                      title="Copy code"
                    />
                  </div>
                </div>
              </div>
              <div class="quick-step">
                <h4 class="step-heading">2) Handle Camera Permissions</h4>
                <div class="code-block">
                  <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">                    
<code><span class="hljs-keyword">private</span> <span class="hljs-keyword">func</span> <span class="hljs-title function_">checkCameraPermission</span>() {
    <span class="hljs-keyword">switch</span> <span class="hljs-type">AVCaptureDevice</span>.authorizationStatus(for: .video) {
    <span class="hljs-keyword">case</span> .authorized: cameraPermissionGranted <span class="hljs-operator">=</span> <span class="hljs-literal">true</span>
    <span class="hljs-keyword">case</span> .notDetermined: requestCameraPermission()
    <span class="hljs-keyword">default</span>: cameraPermissionGranted <span class="hljs-operator">=</span> <span class="hljs-literal">false</span>
    }
}

<span class="hljs-keyword">private</span> <span class="hljs-keyword">func</span> <span class="hljs-title function_">requestCameraPermission</span>() {
    <span class="hljs-type">AVCaptureDevice</span>.requestAccess(for: .video) { granted <span class="hljs-keyword">in</span>
        <span class="hljs-type">DispatchQueue</span>.main.async { cameraPermissionGranted <span class="hljs-operator">=</span> granted }
    }
}</code></span></pre>
                  <div class="code-action">
                    <RiFileCopyLine
                      size="1.3rem"
                      class="copy-icon"
                      @click="copyToClipboard($event.target)"
                      title="Copy code"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 4.4 API References -->
          <section id="ios-api" class="docs-subsection">
            <h3 class="subsection-heading">API References</h3>
            <div class="api-section">
              <h4>Client SDK</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-type">ApiClient</span>.generateSessionId(
    sdkKey: <span class="hljs-type">String</span>,                          <span class="hljs-comment">// Your SDK key</span>
    sceneName: <span class="hljs-type">String</span>? <span class="hljs-operator">=</span> <span class="hljs-literal">nil</span>,                <span class="hljs-comment">// Optional scene name</span>
    metadata: [String: <span class="hljs-type">String</span>]<span class="hljs-operator">?</span> <span class="hljs-operator">=</span> <span class="hljs-literal">nil</span>,       <span class="hljs-comment">// Optional metadata</span>
    completion: <span class="hljs-keyword">@escaping</span> <span class="hljs-type">SessionCreationCompletion</span> <span class="hljs-comment">// Result callback</span>
)</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
              <h4>Response Types</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-keyword">public</span> <span class="hljs-keyword">typealias</span> <span class="hljs-type">SessionCreationCompletion</span> <span class="hljs-operator">=</span> (<span class="hljs-type">ApiError</span>?, <span class="hljs-type">String</span>?) -&gt; <span class="hljs-type">Void</span>
<span class="hljs-keyword">public</span> <span class="hljs-keyword">enum</span> <span class="hljs-title class_">ApiError</span>: <span class="hljs-title class_ inherited__">Error</span> {
    <span class="hljs-keyword">case</span> message(<span class="hljs-type">Int</span>, <span class="hljs-type">String</span>)
}</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
              <h4>Camera</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">
<code><span class="hljs-type">Camera3DView</span>(sessionId: sessionId, sdkKey: sdkKey)</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
              <h4>Upload Management</h4>
              <div class="code-block small">
                <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto">                  
<code><span class="hljs-comment">// Start upload manager (usually automatic)</span>
<span class="hljs-type">UploadManager</span>.sharedInstance.start(sdkKey: sdkKey)

<span class="hljs-comment">// Stop upload manager</span>
<span class="hljs-type">UploadManager</span>.sharedInstance.stop()

<span class="hljs-comment">// Check upload progress</span>
<span class="hljs-type">UploadManager</span>.sharedInstance.getUploadProgress(sessionId: sessionId) { progress <span class="hljs-keyword">in</span>
    <span class="hljs-comment">// -1 = no files, 0..100 = progress, 100 = complete</span>
}</code></span></pre>
                <div class="code-action">
                  <RiFileCopyLine
                    size="1.3rem"
                    class="copy-icon"
                    @click="copyToClipboard($event.target)"
                    title="Copy code"
                  />
                </div>
              </div>
            </div>
          </section>
        </section>

        <!-- 5. Server-side API -->
        <section id="server-api" class="docs-section">
          <h2 class="section-heading"><i>Server-side API</i></h2>
          <div class="api-section">
            <h3>Get Session Status</h3>
            <div class="api-endpoint">
              <div class="endpoint-header">
                <span class="method get">GET</span>
                <span class="url"
                  >https://travvir.com/api/v3/sdk/{sessionId}</span
                >
              </div>
              <p class="endpoint-description">
                Retrieve the status, progress, and download URL for a capture
                session.
              </p>

              <div class="api-details">
                <h4>Headers</h4>
                <div class="code-block small">
                  <pre><span>
<code>Authorization: Bearer {sdk-server-key}</code></span></pre>
                  <div class="code-action">
                    <RiFileCopyLine
                      size="1.3rem"
                      class="copy-icon"
                      @click="copyToClipboard($event.target)"
                      title="Copy code"
                    />
                  </div>
                </div>

                <h4>Response</h4>
                <div class="response-tabs">
                  <div class="tab-headers">
                    <button class="tab-header active" data-tab="success">
                      Success (200)
                    </button>
                    <button class="tab-header" data-tab="error">
                      Error (400)
                    </button>
                  </div>
                  <div class="tab-content active" data-tab="success">
                    <div class="code-block">
                      <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">"status"</span><span class="hljs-punctuation">:</span> <span class="hljs-number">1</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">"data"</span><span class="hljs-punctuation">:</span> <span class="hljs-punctuation">{</span>
    <span class="hljs-attr">"sessionId"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"kPKAOfh52g"</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">"metadata"</span><span class="hljs-punctuation">:</span> <span class="hljs-punctuation">{</span>
      <span class="hljs-attr">"room_type"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"cabin_area"</span><span class="hljs-punctuation">,</span>
      <span class="hljs-attr">"property_id"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"1231780"</span>
    <span class="hljs-punctuation">}</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">"sceneName"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"Cabin Area"</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">"status"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"completed"</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">"progress"</span><span class="hljs-punctuation">:</span> <span class="hljs-number">100</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">"downloadUrl"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"https://example.com/download-url"</span>
  <span class="hljs-punctuation">}</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">"message"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"OK"</span>
<span class="hljs-punctuation">}</span></code></span></pre>
                      <div class="code-action">
                        <RiFileCopyLine
                          size="1.3rem"
                          class="copy-icon"
                          @click="copyToClipboard($event.target)"
                          title="Copy code"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="tab-content" data-tab="error">
                    <div class="code-block">
                      <pre><span class="hljs mb-0 p-4 block min-h-full overflow-auto"><code><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">"status"</span><span class="hljs-punctuation">:</span> <span class="hljs-number">0</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">"message"</span><span class="hljs-punctuation">:</span> <span class="hljs-string">"Job not found"</span>
<span class="hljs-punctuation">}</span></code></span></pre>
                      <div class="code-action">
                        <RiFileCopyLine
                          size="1.3rem"
                          class="copy-icon"
                          @click="copyToClipboard($event.target)"
                          title="Copy code"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="status-table">
              <h4>Session Status Values</h4>
              <table class="docs-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Description</th>
                    <th>Progress Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>"created"</code></td>
                    <td>Session initialized, capture not started</td>
                    <td><code>0</code></td>
                  </tr>
                  <tr>
                    <td><code>"captured"</code></td>
                    <td>3D scanning complete, ready for upload</td>
                    <td><code>0</code></td>
                  </tr>
                  <tr>
                    <td><code>"uploading"</code></td>
                    <td>Files being uploaded to server</td>
                    <td><code>0-100</code> (calculated)</td>
                  </tr>
                  <tr>
                    <td><code>"uploaded"</code></td>
                    <td>Upload complete, queued for processing</td>
                    <td><code>0</code></td>
                  </tr>
                  <tr>
                    <td><code>"processing"</code></td>
                    <td>Server processing the scan data</td>
                    <td><code>0-100</code> (calculated)</td>
                  </tr>
                  <tr>
                    <td><code>"completed"</code></td>
                    <td>Processing finished, result ready</td>
                    <td><code>100</code></td>
                  </tr>
                  <tr>
                    <td><code>"failed"</code></td>
                    <td>Error occurred at any stage</td>
                    <td><code>0</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="docs-section">
          <div class="requirements-grid">
            <div class="requirement-card">
              <h3>Platform Support</h3>
              <ul class="requirement-list">
                <li><strong>Android</strong><i>: API 26+ (Android 8.0)</i></li>
                <li><strong>iOS</strong><i>: iOS 16.0+</i></li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Best Practices (optional short section kept) -->
        <section id="best-practices" class="docs-section">
          <h2 class="section-heading"><i>Best Practices</i></h2>
          <div class="best-practices-grid">
            <div
              class="practice-card"
              v-for="practice in PracticeCard"
              :key="practice.title"
            >
              <h3>{{ practice.title }}</h3>
              <ul>
                <li v-for="point in practice.point" :key="point">
                  {{ point }}
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="license" class="docs-section">
          <h2 class="section-heading"><i>License</i></h2>
          <div class="section-column">
            <h3>License Agreement</h3>
            <p><i>Copyright (c) 2025 Travvir. All rights reserved.</i></p>
            <p>
              <i>
                NOTICE: All information contained herein is, and remains the
                property of Travvir and its suppliers, if any. The intellectual
                and technical concepts contained herein are proprietary to
                Travvir and its suppliers and are protected by trade secret or
                copyright law.
              </i>
            </p>
            <p>
              <i>
                Dissemination of this information or reproduction of this
                material is strictly forbidden unless prior written permission
                is obtained from Travvir.</i
              >
            </p>
          </div>
          <div class="columns">
            <div class="headings">
              <h2>License Grant</h2>
              <p>
                <i>
                  Subject to the terms and conditions of this Agreement, Travvir
                  grants you a limited, non-exclusive, non-transferable,
                  non-sublicensable license to use the Travvir Capture SDK (the
                  "SDK") solely for the purpose of integrating the SDK into your
                  mobile applications.</i
                >
              </p>
            </div>
            <div class="headings">
              <h2>Limitation of Liability</h2>
              <p>
                <i>
                  In no event shall travvir be liable for any claim, damages or
                  other liability, whether in an action of contract, tort or
                  otherwise, arising from, out of or in connection with the sdk
                  or the use or other dealings in the sdk.
                </i>
              </p>
            </div>
            <div class="heading">
              <div class="practice-card">
                <h2>Restrictions</h2>
                <ul>
                  <li>
                    Reverse engineer, decompile, disassemble, or attempt to
                    derive the source code of the SDK
                  </li>
                  <li>
                    Modify, adapt, translate, or create derivative works based
                    on the SDK
                  </li>
                  <li>
                    Distribute, sublicense, rent, lease, or lend the SDK to any
                    third party
                  </li>
                  <li>
                    Remove, alter, or obscure any proprietary notices on the SDK
                  </li>
                  <li>
                    Use the SDK for any purpose other than as expressly
                    permitted herein
                  </li>
                </ul>
              </div>
              <div class="headings">
                <h2>Ownership</h2>
                <p>
                  <i>
                    The SDK and all intellectual property rights therein are and
                    shall remain the sole and exclusive property of Travvir. No
                    title or ownership of the SDK or any intellectual property
                    rights therein is transferred to you.
                  </i>
                </p>
              </div>
              <div class="headings">
                <h2>Disclaimer of Warranties</h2>
                <p>
                  <i>
                    The sdk is provided "as is" without warranty of any kind,
                    express or implied, including but not limited to the
                    warranties of merchantability, fitness for a particular
                    purpose and noninfringement.
                  </i>
                </p>
              </div>
              <div class="headings">
                <h2>Limitation of Liability</h2>
                <p>
                  <i>
                    In no event shall travvir be liable for any claim, damages
                    or other liability, whether in an action of contract, tort
                    or otherwise, arising from, out of or in connection with the
                    sdk or the use or other dealings in the sdk.
                  </i>
                </p>
              </div>
              <div class="headings">
                <h2>Termination</h2>
                <p>
                  <i>
                    This license is effective until terminated. Your rights
                    under this license will terminate automatically without
                    notice if you fail to comply with any term(s) of this
                    Agreement.
                  </i>
                </p>
              </div>
              <div class="headings">
                <h2>Governing Law</h2>
                <p>
                  <i>
                    This Agreement shall be governed by and construed in
                    accordance with the laws of the jurisdiction in which
                    Travvir is established, without regard to its conflict of
                    law provisions.
                  </i>
                </p>
              </div>
              <div class="headings">
                <h2>CONTACT INFORMATION</h2>
                <p>For licensing inquiries, please contact:</p>
                <div class="license-footer">
                  <p><strong>Travvir</strong></p>
                  <p>Email: rohan@travvir.com</p>
                  <p>Website: https://www.travvir.com</p>
                  <p class="agreement-text">
                    <i>
                      By using the SDK, you acknowledge that you have read this
                      Agreement, understand it, and agree to be bound by its
                      terms and conditions.
                    </i>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Footer -->
        <div class="docs-footer">
          <p class="updated">Last updated: September 2025</p>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { RiFileCopyLine } from "@remixicon/vue";
import { onMounted, ref, reactive } from "vue";
import axios from "axios";
const Name = ref("");
const Email = ref("");
const InvalidText = ref("");
const flash = ref(false);
const submit = ref(false);
const config = useRuntimeConfig();
const baseUrl = config.public.NUXT_CDN_URL;
const isPlaying = ref(false);

const submitSDKRequest = async () => {
  try {
    const EmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!EmailPattern.test(Email.value)) {
      flash.value = true;
      InvalidText.value = "Please enter a valid email address.";
      return;
    }
    flash.value = false;
    if (submit.value) return;
    submit.value = true;
    const response = await axios.post(`${config.public.NUXT_API_URL}/contact`, {
      name: Name.value,
      mobile: "000000000",
      email: Email.value,
      message: `
      SDK Access Request
      -------------------
      `,
    });
    if (response.status === 200) {
      Email.value = "";
      Name.value = "";
      flash.value = true;
      submit.value = false;
      InvalidText.value = "Thanks! We'll reach out to you.";
      setTimeout(() => {
        flash.value = false;
      }, 5000);
    }
    flash.value = true;
  } catch (error) {
    flash.value = true;
    console.error("Error submitting form:", error);
    InvalidText.value =
      "There was an error submitting the form. Please try again.";
    setInterval(() => {
      flash.value = false;
    }, 7000);
    submit.value = false;
  }
};

const PracticeCard = [
  {
    title: "Session Management",
    point: [
      "Always create a new session for each scan",
      "Use descriptive scene names and include relevant metadata",
    ],
  },
  {
    title: "Permission Handling",
    point: [
      "Request permissions before launching camera",
      "Handle permission denials gracefully with guidance",
    ],
  },
];

const CardDetails = [
  {
    title: "Version",
    Detail: [
      {
        label: "Minimum SDK",
        link: " Android API 26 (Android 8.0)",
      },
    ],
  },
  {
    title: "Permissions",
    Detail: [
      {
        label: "Camera",
      },
      {
        label: "Storage",
        link: " (Android 10 & below)",
      },
      {
        label: "Notifications",
        link: " (Android 13+)",
      },
    ],
  },
  {
    title: "Hardwares",
    Detail: [
      {
        label: "Camera",
      },
      {
        label: "Accelerometer",
      },
      {
        label: "Gyroscope",
      },
    ],
  },
];
// Copy to clipboard functionality
const copyToClipboard = async (element) => {
  try {
    // Find the code element within the clicked element's parent
    const codeBlock = element.closest(".code-block");
    const codeElement = codeBlock.querySelector("pre code");

    if (codeElement) {
      // Get text content without HTML tags
      const textToCopy = codeElement.textContent || codeElement.innerText;

      // Use modern clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }

      // Visual feedback
      const copyIcon = element;
      copyIcon.style.color = "#22c55e"; // Green color for success

      // Reset color after 2 seconds
      setTimeout(() => {
        copyIcon.style.color = "#b8b8b8";
      }, 2000);

      // Optional: Show toast notification (you can customize this)
      console.log("Code copied to clipboard!");
    }
  } catch (err) {
    console.error("Failed to copy code: ", err);
    // Visual feedback for error
    const copyIcon = element;
    const originalColor = copyIcon.style.color;
    copyIcon.style.color = "#ef4444"; // Red color for error

    setTimeout(() => {
      copyIcon.style.color = "#b8b8b8";
    }, 2000);
  }
};

const sidebarArr = [
  { title: "Overview", link: "#overview" },
  { title: "Request Access", link: "#request-access" },
  {
    title: "Android SDK",
    link: "#android-sdk",
    children: [
      { title: "Requirements", link: "#android-requirements" },
      { title: "Installation", link: "#android-installation" },
      { title: "Quick Start", link: "#android-quick-start" },
      { title: "API", link: "#android-api" },
    ],
  },
  {
    title: "iOS SDK",
    link: "#ios-sdk",
    children: [
      { title: "Requirements", link: "#ios-requirements" },
      { title: "Installation", link: "#ios-installation" },
      { title: "Quick Start", link: "#ios-quick-start" },
      { title: "API", link: "#ios-api" },
    ],
  },
  { title: "Server-side API", link: "#server-api" },
  { title: "Best Practices", link: "#best-practices" },
  { title: "License", link: "#license" },
];
// Per-platform requirement cards are rendered inline in sections above
// SEO Meta
useSeoMeta({
  title: "Docs | Travvir Studio SDK (Android & iOS)",
  description:
    "Official documentation for Travvir Studio SDK on Android and iOS: requirements, installation, quick start, APIs, and server endpoints.",
  keywords:
    "Travvir SDK, Android SDK, iOS SDK, 3D scanning, documentation, API reference, mobile development",
  ogTitle: "Travvir Studio SDK Documentation",
  ogDescription:
    "Developer documentation for integrating 3D scanning capabilities into Android and iOS applications",
});

// // Smooth scrolling for anchor links
onMounted(() => {
  // Handle tab switching for API responses
  const tabHeaders = document.querySelectorAll(".tab-header");
  const tabContents = document.querySelectorAll(".tab-content");

  tabHeaders.forEach((header) => {
    header.addEventListener("click", () => {
      const tabName = header.dataset.tab;

      // Remove active class from all tabs
      tabHeaders.forEach((h) => h.classList.remove("active"));
      tabContents.forEach((c) => c.classList.remove("active"));

      // Add active class to clicked tab and corresponding content
      header.classList.add("active");
      document
        .querySelector(`[data-tab="${tabName}"].tab-content`)
        .classList.add("active");
    });
  });

  // Smooth scrolling for navigation links with proper offset
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute("href"));
      if (target) {
        // Calculate offset for fixed header and padding
        const headerOffset = 100; // Adjust this value based on your header height + desired padding
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    });
  });
});
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Google+Sans+Code:ital,wght@0,300..800;1,300..800&display=swap");
.docs-container {
  background: #000;
  color: #fff;
  min-height: fit-content;
  font-family: "Inter", "Helvetica Neue", sans-serif;
}

.docs-main {
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  // padding-top: 70px;
  min-height: 100vh;
}

.docs-sidebar {
  width: 280px;
  position: sticky;
  top: 70px;
  height: calc(100vh - 70px);
  overflow-y: auto;
  border-right: 1px solid rgba(255, 255, 255, 0.1);

  @include respond-below(lg) {
    display: none;
  }

  .sidebar-content {
    padding: 2rem 1.5rem;
  }

  .sidebar-title {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .nav-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 0.5rem;
    }

    .nav-item {
      font-size: 1.2rem;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.7;
      text-decoration: unset;
      margin-right: 2rem;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      &:before {
        content: "";
        position: absolute;
        bottom: 0;
        left: 50%;
        width: 0;
        height: 2px;
        background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
        transition: all 0.3s ease;
        transform: translateX(-50%);
      }
      &.router-link-active:before {
        width: 80%;
      }
      &.active:before {
        width: 80%;
      }

      &:hover:before {
        width: 80%;
      }

      @include respond-below(sm) {
        padding: 0.2rem 0.4rem;
        border-radius: 4px;
        font-size: 0.75rem;
      }
    }

    .sub-nav-list {
      list-style: none;
      padding-left: 1rem;
      margin-top: 0.25rem;

      li {
        margin: 0.2rem 0;
      }

      .nav-item.sub {
        font-size: 1rem;
        color: rgba(255, 255, 255, 0.75);
      }
    }
  }
}

.docs-content {
  flex: 1;
  padding: 3rem 3rem 6rem;
  max-width: calc(100% - 280px);

  @include respond-below(lg) {
    max-width: 100%;
    padding: 2rem 2rem 4rem;
  }

  @include respond-below(md) {
    padding: 2rem 1rem 4rem;
  }
}

.docs-section {
  margin-bottom: 4rem;
  scroll-margin-top: 100px; // Modern CSS solution for scroll offset

  &:last-child {
    margin-bottom: 0;
  }
  .container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: fit-content;
    max-width: 800px;
    align-items: center;
    @include respond-below(sm) {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
    }
    .demo-video {
      width: 50%; // Takes 50% width on desktop
      height: fit-content;
      border-radius: 12px;
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
      margin-bottom: 2rem;
      display: flex;
      place-items: center;

      // Tablet/iPad: Adjust width for medium screens
      @include respond-below(md) {
        width: 60%; // Slightly wider on tablets
      }

      // iPhone & Mobile: Full width for mobile devices
      @include respond-below(sm) {
        width: 100%; // Full width on mobile
        margin-bottom: 1.5rem; // Reduced margin for mobile
      }
      .video-app {
        position: relative;
        border-radius: 20px;
        overflow: hidden;
        width: 70%;

        // Large Desktop: Optimized for 27"+ monitors and ultra-wide screens
        height: calc(
          17.5vw * 16 / 9
        ); // 16:9 aspect ratio based on viewport width
        min-height: 300px; // Minimum height for large desktops
        max-height: 500px; // Maximum height to prevent overflow on ultra-wide screens

        // Mac & Medium Desktop: Standard desktop and laptop screens (1440px-1920px)
        @include respond-below(lg) {
          width: 80%; // Slightly wider on medium screens
          height: calc(20vw * 16 / 9); // Adjusted height for medium screens
          min-height: 280px; // Minimum height for Mac/medium desktops
          max-height: 450px; // Maximum height for Mac/medium desktops
        }

        // Tablet/iPad: Medium screens (768px-1024px)
        @include respond-below(md) {
          width: 90%; // Wider on tablets
          height: calc(25vw * 16 / 9); // Increased height ratio for tablets
          min-height: 250px; // Minimum height for tablets
          max-height: 380px; // Maximum height for tablets
        }

        // iPhone & Mobile: Small screens (up to 767px)
        @include respond-below(sm) {
          width: 100%; // Full width on mobile
          height: calc(100vh * 9 / 16); // 16:9 ratio accounting for padding
          min-height: 200px; // Minimum height for very small phones
        }

        &:deep(video) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: contain; // Maintain aspect ratio while fitting container
          border-radius: 1rem;
        }
        &:deep(img) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      .clean-chip {
        position: absolute;
        top: 12%;
        left: 40%;
        z-index: 10;
        transform: translate(-50%, -45%);
        @include respond-below(lg) {
          top: 12%;
          left: 42%;
        }
        .chip-content {
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          padding: 0.5rem 1rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3),
            0 1px 4px rgba(0, 0, 0, 0.15);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          @include respond-below(lg) {
            padding: 0.45rem 0.8rem;
          }
          @include respond-below(sm) {
            padding: 0.4rem 0.5rem;
          }
        }
        .chip-content:hover {
          background: rgba(0, 0, 0, 0.65);
          border-color: rgba(255, 255, 255, 0.25);
          transform: translateY(-1px);
        }

        .sub-text {
          display: grid;
          place-items: center;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 0.9575rem;
          color: #b1b1b1;
          line-height: 1.2;
          @include respond-below(lg) {
            font-size: 0.7rem;
          }
          @include respond-below(sm) {
            font-size: 0.575rem;
          }
        }
      }
    }
    .description {
      width: 50%;
      height: fit-content;
      background-color: #000;
      p {
        font-size: 1rem;
        line-height: 1.6;
        color: #cccccc;
        margin-bottom: 1.5rem;
        font-style: italic;
      }
      @include respond-below(sm) {
        width: 100%;
      }
    }
  }
}

.docs-subsection {
  margin-bottom: 3rem;
  scroll-margin-top: 100px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-heading,
.section-title {
  font-size: 1.8rem;
  font-weight: 500;
  margin-bottom: 1.8rem;
  background-image: linear-gradient(
    90deg,
    #fff 1.51%,
    #5d5be6 18.01%,
    #fff 82.09%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 0.5rem;
  @include respond-below(sm) {
    font-size: 1.6rem;
  }
}

.subsection-heading {
  font-size: 1.45rem;
  // background: #111111;
  font-weight: 500;
  margin-bottom: 1.8rem;
  margin-top: 1rem;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 0.75rem;
}

.step-heading {
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
  margin-bottom: 1rem;
  margin-top: 2rem;

  &:first-child {
    margin-top: 0;
  }
}

.section-intro {
  font-size: 0.975rem;
  line-height: 1.4;
  color: #999999;
  margin-bottom: 2rem;
  max-width: 800px;
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.requirement-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(102, 126, 234, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);

    &::before {
      opacity: 1;
    }
  }

  h3 {
    font-size: 1.3rem;
    font-weight: 500;
    color: #fffffff0;
    margin-bottom: 1rem;
  }

  .requirement-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      align-items: center;
      padding: 0.5rem 0;
      color: #b8b8b8;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      position: relative;
      padding-left: 1rem;
      transition: all 0.3s ease;

      &:last-child {
        border-bottom: none;
      }

      &::before {
        align-items: center;
        content: "•";
        position: absolute;
        left: 0;
        // top: 0.75rem;
        color: #ebecf3;
        font-weight: 600;
        font-size: 0.9rem;
      }
      strong {
        color: rgb(255, 255, 255, 0.9);
        font-weight: 500;
        font-size: 0.9275rem;
        line-height: 1.2;
        letter-spacing: 0.5px;
      }

      i {
        color: rgba(255, 255, 255, 0.7);
        font-style: normal;
      }
    }
  }
}

.installation-steps {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.step {
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @include respond-below(md) {
    gap: 1rem;
  }
}

.step-content {
  flex: 1;

  h3 {
    margin-bottom: 1rem;
    font-size: 1.6rem;
    font-weight: 500;
    color: #ffffff;
  }

  h4 {
    margin-bottom: 1rem;
    font-weight: 500;
    color: #ffffff;
  }

  p {
    font-size: 1rem;
    color: rgb(255, 255, 255, 0.8);
    line-height: 1.6;
    margin-bottom: 1.5rem;
    span {
      // font-family: "Fira Code", monospace;
      padding: 0.2em 0.5em;
      border-radius: 4px;
      font-size: 0.9em;
      color: #aac8e4;
    }
  }

  // Enhanced list styling
  ol,
  ul {
    margin: 1.5rem 0;
    padding-left: 0;
    list-style: none;

    li {
      position: relative;
      padding: 0.75rem 0 0.75rem 2.5rem;
      margin-bottom: 0.5rem;
      color: rgba(255, 255, 255, 0.9);
      line-height: 1.6;
      border-left: 2px solid transparent;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.02);
        border-left-color: #667eea;
        transform: translateX(4px);
      }

      code {
        background: rgba(102, 126, 234, 0.1);
        color: #9bb3ff;
        padding: 0.2em 0.5em;
        border-radius: 4px;
        font-size: 0.9em;
      }

      b {
        color: #fff;
        font-weight: 600;
      }
    }
  }

  // Ordered list numbers
  ol {
    counter-reset: step-counter;

    li {
      counter-increment: step-counter;

      &::before {
        content: counter(step-counter);
        position: absolute;
        left: 0;
        top: 0.75rem;
        width: 1.75rem;
        height: 1.75rem;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
      }
    }
  }

  // Unordered list bullets
  ul {
    li {
      &::before {
        content: "•";
        position: absolute;
        left: 0;
        top: 0.75rem;
        width: 1.5rem;
        height: 1.5rem;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2rem;
        font-weight: 600;
      }
    }
  }
}

.code-block {
  position: relative;
  background: linear-gradient(135deg, #0d1117 0%, #161b22 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  overflow: hidden;
  @include custom-scroll-bar;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #667eea, transparent);
  }

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4);
    transform: translateY(-2px);
  }

  &.small {
    padding: 1rem;
    margin: 1rem 0;
  }

  .code-action {
    position: absolute;
    top: 1rem;
    right: 1rem;
    display: grid;
    justify-content: flex-start;
    height: auto;
    width: fit-content;
    z-index: 2;
  }

  pre {
    margin: 0;
    font-family: "Google Sans Code", monospace;
    font-weight: 500;
    font-size: 1em;
    font-size: 0.9rem;
    line-height: 1.6;
    color: #e6edf3;
    background: transparent;
    flex: 1;
    overflow-x: auto;
    padding-right: 3rem; // Space for copy button

    code {
      color: #e6edf3;
      background: none;
      padding: 0;
      border-radius: 0;
      font-size: inherit;
      white-space: pre;
      tab-size: 2;
    }
  }

  .copy-icon {
    font-size: 1.1rem;
    // color: rgb(, 255, 255);
    // color: rgba(102, 126, 234, 0.7);
    cursor: pointer;
    transition: all 0.3s ease;
    // background: rgba(102, 126, 234, 0.1);
    // padding: 0.5rem;
    border-radius: 6px;

    &:hover {
      // background: rgba(102, 126, 234, 0.2);
      // border-color: rgba(102, 126, 234, 0.4);
      transform: scale(1.1);
    }
  }
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 0.2em 0.5em;
  border-radius: 4px;
  font-size: 0.9em;

  color: #e5e5e5;
}

.quick-start-steps {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.quick-step {
  h3 {
    font-size: 1.6rem;
    font-weight: 500;
    color: #fff;
    margin-bottom: 1rem;
  }

  p {
    color: #b8b8b8;
    line-height: 1.6;
    font-size: 1.05rem;
    margin-bottom: 1.5rem;
  }
}

.api-section {
  margin-bottom: 3rem;
  h4 {
    font-size: 1.25rem;
    font-weight: 400;
    color: #fff;
    margin-bottom: 1rem;
  }
}

.api-endpoint {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.endpoint-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @include respond-below(sm) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .method {
    padding: 0.3rem 0.8rem;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;

    &.get {
      background: #10b981;
      color: #fff;
    }
  }

  .url {
    font-family: "Fira Code", monospace;
    font-size: 0.9rem;
    color: #e5e5e5;
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 6px;
    flex: 1;

    @include respond-below(sm) {
      width: 100%;
      overflow-x: auto;
      @include custom-scroll-bar;
      word-break: break-all;
    }
  }
}

.endpoint-description {
  color: #b8b8b8;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.api-details {
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin: 2rem 0 1rem;

    &:first-child {
      margin-top: 0;
    }
  }
}

.response-tabs {
  margin-top: 1rem;

  .tab-headers {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;

    .tab-header {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #b8b8b8;
      padding: 0.7rem 1.2rem;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.9rem;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
      }

      &.active {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: #fff;
        border-color: transparent;
      }
    }
  }

  .tab-content {
    display: none;

    &.active {
      display: block;
    }
  }
}
.status-table {
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  @include custom-scroll-bar;
  h4 {
    margin-bottom: 1rem;
    font-size: 1.6rem;
    font-weight: 700;
    color: #ffffff;
  }
}

.docs-table {
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  overflow: hidden;

  th,
  td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  th {
    background: rgba(255, 255, 255, 0.05);
    font-weight: 600;
    color: #fff;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    color: #b8b8b8;
    font-size: 0.9rem;

    code {
      font-size: 0.8rem;
    }
  }

  tr:last-child {
    th,
    td {
      border-bottom: none;
    }
  }
}

.advanced-section {
  margin-bottom: 3rem;

  h3 {
    font-size: 1.4rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
  }

  p {
    color: #b8b8b8;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
}

.best-practices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;

  @include respond-below(md) {
    grid-template-columns: 1fr;
  }
}

.practice-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 500;
    color: #fff;
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 0.8rem 0;
      color: #b8b8b8;
      line-height: 1.6;
      position: relative;
      padding-left: 1rem;

      &::before {
        content: "•";
        position: absolute;
        left: 0;
        color: #ffffff;
        font-weight: 600;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }
  }
}

// License Section Styles
.section-column {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;

  h3 {
    font-size: 1.3rem;
    font-weight: 300;
    color: #fff;
  }

  > p {
    color: #b8b8b8;
    line-height: 1.8;
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }
}

.columns {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  .heading {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .headings {
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 1.8rem;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba(102, 126, 234, 0.3);
      transform: translateX(4px);
    }

    h2 {
      font-size: 1.25rem;
      font-weight: 500;
      color: #fff;
      margin-bottom: 1rem;
      letter-spacing: 0.5px;
    }

    p {
      color: #b8b8b8;
      line-height: 1.3;
      font-size: 1rem;
      // text-transform: capitalize;
      margin-bottom: 0.8rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.license-footer {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  p {
    color: #b8b8b8;
    line-height: 1.7;
    font-size: 1.05rem;
    margin-bottom: 0.6rem;

    strong {
      color: #fff;
      font-weight: 500;
      font-size: 1.5rem;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &.agreement-text {
      margin-top: 1.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      color: #999;
      font-style: italic;
      font-size: 1rem;
    }
  }
}

.docs-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
  margin-top: 4rem;
  text-align: center;

  p {
    color: #666;
    margin: 0.5rem 0;
    font-size: 0.9rem;

    strong {
      color: #999;
    }
  }
}

// Scrollbar styling
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }
}

// SDK Access Request Form Styles
.sdk-request-form {
  margin-bottom: 4rem;

  .form-container {
    max-width: 500px;
    border-radius: 16px;

    @include respond-below(md) {
      padding-right: 2rem;
    }
  }

  .request-form {
    .form-group {
      margin-bottom: 1.5rem;

      label {
        display: block;
        font-weight: 600;
        color: #fff;
        margin-bottom: 0.7rem;
        font-size: 0.9rem;
      }

      input,
      textarea {
        width: 100%;
        padding: 1rem 1.2rem;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        color: #fff;
        font-size: 1rem;
        transition: all 0.3s ease;
        font-family: inherit;

        &::placeholder {
          color: #666;
        }

        &:focus {
          outline: none;
          border-color: #667eea;
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
      }

      textarea {
        resize: vertical;
        min-height: 120px;
        line-height: 1.5;
      }
    }

    .submit-btn {
      width: 100%;
      padding: 1rem 2rem;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 8px;
      color: #fff;
      font-weight: 600;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.3s ease;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
    }

    .form-note {
      text-align: left;
      color: #999;
      font-size: 0.95rem;
      margin-top: 1.5rem;
      margin-bottom: 0;

      i {
        font-style: italic;
      }
    }
  }
}
pre code.hljs {
  @include custom-scroll-bar;
  display: block;
  overflow-x: auto;
  padding: 1em;
}
code.hljs {
  padding: 3px 5px;
}
.hljs {
  color: #c9d1d9;
}
.hljs-doctag,
.hljs-keyword,
.hljs-meta .hljs-keyword,
.hljs-template-tag,
.hljs-template-variable,
.hljs-type,
.hljs-variable.language_ {
  color: #ff7b72;
}
.hljs-title,
.hljs-title.class_,
.hljs-title.class_.inherited__,
.hljs-title.function_ {
  color: #d2a8ff;
}
.hljs-attr,
.hljs-attribute,
.hljs-literal,
.hljs-meta,
.hljs-number,
.hljs-operator,
.hljs-selector-attr,
.hljs-selector-class,
.hljs-selector-id,
.hljs-variable {
  color: #79c0ff;
}
.hljs-meta .hljs-string,
.hljs-regexp,
.hljs-string {
  color: #a5d6ff;
}
.hljs-built_in,
.hljs-symbol {
  color: #ffa657;
}
.hljs-code,
.hljs-comment,
.hljs-formula {
  color: #8b949e;
}
.hljs-name,
.hljs-quote,
.hljs-selector-pseudo,
.hljs-selector-tag {
  color: #7ee787;
}
.hljs-subst {
  color: #c9d1d9;
}
.hljs-section {
  color: #1f6feb;
  font-weight: 700;
}
.hljs-bullet {
  color: #f2cc60;
}
.hljs-emphasis {
  color: #c9d1d9;
  font-style: italic;
}
.hljs-strong {
  color: #c9d1d9;
  font-weight: 700;
}
.hljs-addition {
  color: #aff5b4;
  background-color: #033a16;
}
.hljs-deletion {
  color: #ffdcd7;
  background-color: #67060c;
}
span {
  font-size: 14px;
  font-weight: lighter;
}
.notes {
  font-size: 1.1rem;
  color: #fff;
  font-weight: 400;
  line-height: 1.6;
  .note-content {
    display: flex;
    gap: 0.5rem;
  }
}
</style>
